import L from "leaflet";
import { useEffect, useRef } from "react";
import type { ItineraryStop, RouteLeg } from "../types";

interface MapPanelProps {
  stops: ItineraryStop[];
  selectedStopId: string | null;
  onSelect: (stopId: string) => void;
  routeLegs?: RouteLeg[];
  title?: string;
  onHover?: (stopId: string | null) => void;
  onAddPoint?: (latitude: number, longitude: number) => void;
}

function fitMapToStops(map: L.Map, stops: ItineraryStop[]) {
  map.invalidateSize({ animate: false, pan: false });
  const coordinates = stops.map((stop) => L.latLng(stop.lat, stop.lon));
  if (coordinates.length) map.fitBounds(L.latLngBounds(coordinates).pad(0.22), { maxZoom: 14, animate: false });
  else map.setView([20, 0], 2, { animate: false });
}

export function MapPanel({ stops, selectedStopId, onSelect, routeLegs = [], title = "Trip route map", onHover, onAddPoint }: MapPanelProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const routeLayerRef = useRef<L.FeatureGroup | null>(null);
  const markersRef = useRef(new Map<string, L.Marker>());
  const stopsRef = useRef(stops);
  stopsRef.current = stops;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onAddPointRef = useRef(onAddPoint);
  onAddPointRef.current = onAddPoint;

  useEffect(() => {
    if (!hostRef.current || mapRef.current) return;
    const map = L.map(hostRef.current, { zoomControl: true, attributionControl: true }).setView([20, 0], 2);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    routeLayerRef.current = L.featureGroup().addTo(map);
    map.on("click", (event) => onAddPointRef.current?.(event.latlng.lat, event.latlng.lng));
    mapRef.current = map;

    let firstFrame = 0;
    let secondFrame = 0;
    const syncLayout = () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      firstFrame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(() => fitMapToStops(map, stopsRef.current));
      });
    };
    const observer = new ResizeObserver(syncLayout);
    observer.observe(hostRef.current);
    syncLayout();
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      map.remove();
      mapRef.current = null;
      routeLayerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const group = routeLayerRef.current;
    if (!map || !group) return;
    group.clearLayers();
    markersRef.current.clear();
    const coordinates: L.LatLngExpression[] = [];

    stops.forEach((stop, index) => {
      const marker = L.marker([stop.lat, stop.lon], {
        icon: L.divIcon({
          className: "roam-marker-wrap",
          html: `<span class="roam-marker"><b>${index + 1}</b></span>`,
          iconSize: [34, 42],
          iconAnchor: [17, 40],
        }),
        keyboard: true,
        title: `Stop ${index + 1}: ${stop.name}`,
      });
      const label = document.createElement("span");
      label.textContent = `${index + 1}. ${stop.name}`;
      marker.bindTooltip(label, { direction: "top", offset: [0, -30] });
      marker.on("click", () => onSelectRef.current(stop.id));
      marker.on("mouseover", () => onHover?.(stop.id));
      marker.on("mouseout", () => onHover?.(null));
      marker.addTo(group);
      markersRef.current.set(stop.id, marker);
      coordinates.push([stop.lat, stop.lon]);
    });

    const visibleLegs = routeLegs.filter((leg) => stops.some((stop) => stop.id === leg.fromStopId) && stops.some((stop) => stop.id === leg.toStopId));
    if (visibleLegs.length) {
      visibleLegs.forEach((leg) => L.polyline(
        leg.geometry.map(([lon, lat]) => [lat, lon] as L.LatLngExpression),
        {
          color: "#7b450c",
          weight: 3,
          opacity: 0.82,
          dashArray: leg.source === "provider" ? undefined : leg.source === "cached" ? "8 7" : "2 8",
          lineCap: "round",
          lineJoin: "round",
        },
      ).addTo(group));
    } else if (coordinates.length > 1) {
      L.polyline(coordinates, { color: "#7b450c", weight: 3, opacity: 0.82, dashArray: "7 8", lineJoin: "round" }).addTo(group);
    }
    fitMapToStops(map, stops);
  }, [routeLegs, stops]);

  useEffect(() => {
    if (!selectedStopId) return;
    const marker = markersRef.current.get(selectedStopId);
    if (!marker || !mapRef.current) return;
    marker.openTooltip();
    mapRef.current.panTo(marker.getLatLng(), { animate: true, duration: 0.25 });
  }, [selectedStopId]);

  return (
    <section className="map-panel" aria-label={title}>
      <div ref={hostRef} className="map-canvas" />
      {!stops.length ? <div className="map-empty"><strong>The route will appear here.</strong><span>Add a real place to draw the first point.</span></div> : null}
      {routeLegs.some((leg) => leg.providerId) ? <p className="map-route-attribution">Routes: OpenRouteService by HeiGIT · © OpenStreetMap contributors</p> : null}
    </section>
  );
}
